import { Alert } from "react-native";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import { usePrimaryWorkspace, useWorkspaceAssets } from "~/hooks/workspace";
import { formatCurrency } from "~/utils/format";
import { nzIncomeYear } from "~/utils/nz-finance";

const methodOptions = [
  { label: "Diminishing value (DV)", value: "diminishing_value" },
  { label: "Straight line (SL)", value: "straight_line" },
  { label: "Do not depreciate", value: "none" },
];

export default function AssetsScreen() {
  const workspace = usePrimaryWorkspace();
  const workspaceId = workspace.data?.id;
  const { readOnly } = useWorkspaceReadOnly(workspaceId);
  const assets = useWorkspaceAssets(workspaceId);
  const incomeYear = nzIncomeYear(new Date());
  const currentEntries = assets.depreciationEntries.filter(
    (entry) => entry.income_year === incomeYear,
  );
  const entryByAsset = new Map(
    currentEntries.map((entry) => [entry.asset_id, entry]),
  );
  const costTotal = assets.assets.reduce(
    (sum, asset) => sum + asset.original_cost_cents,
    0,
  );
  const claimTotal = currentEntries.reduce(
    (sum, entry) =>
      sum + entry.investment_boost_claim_cents + entry.depreciation_claim_cents,
    0,
  );

  const fields = [
    { key: "name", label: "Asset name", placeholder: "MacBook Pro" },
    { key: "assetNumber", label: "Asset number", placeholder: "FA-001" },
    { key: "category", label: "Category", placeholder: "Computer equipment" },
    { key: "purchaseDate", label: "Purchase date", placeholder: "YYYY-MM-DD" },
    {
      key: "inServiceDate",
      label: "Available for use",
      placeholder: "YYYY-MM-DD",
    },
    {
      key: "originalCost",
      label: "Original cost",
      placeholder: "2999.00",
      keyboardType: "numeric" as const,
    },
    {
      key: "gst",
      label: "GST in cost",
      placeholder: "391.17",
      keyboardType: "numeric" as const,
    },
    {
      key: "depreciableCost",
      label: "Depreciable cost",
      placeholder: "Cost less GST if GST registered",
      keyboardType: "numeric" as const,
    },
    {
      key: "depreciationMethod",
      label: "Method",
      placeholder: "Diminishing value",
      options: methodOptions,
    },
    {
      key: "depreciationRate",
      label: "IRD rate %",
      placeholder: "30",
      keyboardType: "numeric" as const,
    },
    {
      key: "businessUsePercent",
      label: "Business use %",
      placeholder: "100",
      keyboardType: "numeric" as const,
    },
    {
      key: "investmentBoost",
      label: "Investment Boost eligible",
      placeholder: "No",
      options: [
        { label: "No", value: "false" },
        { label: "Yes — verify eligibility", value: "true" },
      ],
    },
    { key: "disposalDate", label: "Disposal date", placeholder: "YYYY-MM-DD" },
    {
      key: "disposalProceeds",
      label: "Disposal proceeds",
      placeholder: "0.00",
      keyboardType: "numeric" as const,
    },
    {
      key: "description",
      label: "Description",
      placeholder: "Model, serial number and use",
      multiline: true,
    },
    {
      key: "notes",
      label: "Tax notes",
      placeholder: "Rate source, private-use basis, disposal notes",
      multiline: true,
    },
  ];

  return (
    <ResourceListScreen
      title="Assets & depreciation"
      subtitle={`Fixed-asset register with DV/SL estimates for the ${incomeYear} income year. Rates, eligibility, private use and disposal treatment need accountant review before filing.`}
      primaryAction="Add fixed asset"
      emptyTitle="Fixed assets"
      emptyDescription="Add equipment, vehicles and other capital assets kept longer than one year."
      fields={fields}
      requiredField="name"
      readOnly={readOnly}
      metrics={[
        { label: "Assets", value: String(assets.assets.length) },
        { label: "Original cost", value: formatCurrency(costTotal / 100) },
        {
          label: `${incomeYear} estimated claims`,
          value: formatCurrency(claimTotal / 100),
        },
      ]}
      items={assets.assets.map((asset) => {
        const entry = entryByAsset.get(asset.id);
        return {
          id: asset.id,
          eyebrow: `${asset.asset_number ?? "No asset number"} · ${asset.category}`,
          title: asset.name,
          description: asset.description ?? undefined,
          meta: [
            `Cost ${formatCurrency(asset.original_cost_cents / 100)}`,
            `${asset.depreciation_method.replaceAll("_", " ")} ${Number(asset.depreciation_rate)}%`,
            `${Number(asset.business_use_percent)}% business use`,
            asset.investment_boost
              ? "Investment Boost selected"
              : "Standard depreciation",
            entry
              ? `${incomeYear} estimate ${formatCurrency((entry.investment_boost_claim_cents + entry.depreciation_claim_cents) / 100)}`
              : `${incomeYear} estimate not saved`,
            asset.disposal_date ? `Disposed ${asset.disposal_date}` : "Active",
          ],
          editValues: {
            name: asset.name,
            assetNumber: asset.asset_number ?? "",
            category: asset.category,
            purchaseDate: asset.purchase_date,
            inServiceDate: asset.in_service_date,
            originalCost: (asset.original_cost_cents / 100).toFixed(2),
            gst: (asset.gst_cents / 100).toFixed(2),
            depreciableCost: (asset.depreciable_cost_cents / 100).toFixed(2),
            depreciationMethod: asset.depreciation_method,
            depreciationRate: String(asset.depreciation_rate),
            businessUsePercent: String(asset.business_use_percent),
            investmentBoost: String(asset.investment_boost),
            disposalDate: asset.disposal_date ?? "",
            disposalProceeds:
              asset.disposal_proceeds_cents === null
                ? ""
                : (asset.disposal_proceeds_cents / 100).toFixed(2),
            description: asset.description ?? "",
            notes: asset.notes ?? "",
          },
          actions: readOnly ? undefined : (
            <>
              <Button
                compact
                variant="outline"
                disabled={assets.saveEstimate.isPending}
                onPress={() =>
                  assets.saveEstimate.mutate({ asset, incomeYear })
                }
              >
                <ButtonText>
                  {entry
                    ? `Recalculate ${incomeYear}`
                    : `Estimate ${incomeYear}`}
                </ButtonText>
              </Button>
              <Button
                compact
                variant="outline"
                onPress={() =>
                  Alert.alert(
                    "Delete fixed asset?",
                    "Its saved depreciation schedule will also be deleted.",
                    [
                      { text: "Cancel", style: "cancel" },
                      {
                        text: "Delete",
                        style: "destructive",
                        onPress: () => assets.remove.mutate(asset.id),
                      },
                    ],
                  )
                }
              >
                <ButtonText>Delete</ButtonText>
              </Button>
            </>
          ),
        };
      })}
      loading={workspace.isLoading || assets.isLoading}
      error={
        assets.error ??
        assets.create.error ??
        assets.update.error ??
        assets.saveEstimate.error ??
        assets.remove.error
      }
      creating={assets.create.isPending}
      updating={assets.update.isPending}
      onCreate={(values) => assets.create.mutateAsync(values)}
      onUpdate={(id, values) => assets.update.mutateAsync({ id, values })}
    />
  );
}
